// @flow
import React, { useContext } from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';
import { messageFormat } from '@clubmed/utils';

import { t } from 'Src/i18n';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { getGoogleMapURL } from 'Components/utils/google/google.utils';
import { getBaiduMapURL } from 'Components/utils/baidu/baidu.utils';

import { Address, type AddressProps } from 'Components/frameworks/address/Address.jsx';
import { BaiduMap } from 'Components/frameworks/map/BaiduMap.jsx';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { GoogleMap } from 'Components/frameworks/map/GoogleMap.jsx';
import { SeeMore } from 'Components/frameworks/see-more/SeeMore.jsx';
import { GoogleStreetView } from 'Components/frameworks/map/GoogleStreetView.jsx';
import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

type Props = {
  access?: Array<{
    iconName?: IconName,
    texts?: Array<string>,
  }>,
  accessTitle?: string,
  address?: AddressProps,
  className?: string,
  isBaidu?: boolean,
  map?: *,
  productName?: string,
  streetView?: {
    fov?: number,
    heading?: number,
    latitude: number,
    longitude: number,
    pitch?: number,
  },
};

export function AccessAndMap(props: Props) {
  const {
    access = [],
    accessTitle,
    address,
    className,
    isBaidu,
    map,
    productName,
    streetView = {},
  } = props;
  const { isTablet } = useContext(ResponsiveContext);

  if (!map) {
    return null;
  }

  const ratio = isTablet
    ? {
        w: 780,
        h: 420,
      }
    : {
        w: 290,
        h: 320,
      };

  const mapProps = {
    ...map,
    closeLabel: t('common.close'),
    width: ratio.w,
    height: ratio.h,
  };

  const coords = {
    latitude: mapProps.latitude,
    longitude: mapProps.longitude,
  };

  const streetViewRatio = {
    w: 170,
    h: 90,
  };

  const mapUrl = isBaidu ? getBaiduMapURL(coords) : getGoogleMapURL(coords);

  const hasStreetView = !!streetView.latitude && !!streetView.longitude;

  return (
    <div className={classnames(className, 'flex flex-col sm:flex-row')}>
      <div className="relative mb-5 sm:mb-0 sm:w-2/3">
        {isBaidu ? (
          <BaiduMap
            {...mapProps}
            className="rounded-tiny"
            title={messageFormat(t('common.maps.productBaiduMap')).format({ productName })}
          />
        ) : (
          <div className="relative">
            <GoogleMap
              {...mapProps}
              className="rounded-tiny"
              title={messageFormat(t('common.maps.productGoogleMap')).format({ productName })}
            />
            {hasStreetView && (
              <div
                style={{
                  height: streetViewRatio.h,
                  width: streetViewRatio.w,
                }}
                className="absolute bottom-0 right-0 m-5"
              >
                {/* $FlowFixMe */}
                <GoogleStreetView
                  {...streetView}
                  className="border-5 border-solid border-white rounded-tiny shadow z-1"
                  closeLabel={t('common.close')}
                  height={streetViewRatio.h}
                  title={messageFormat(t('common.maps.productGoogleStreetView')).format({
                    productName,
                  })}
                  width={streetViewRatio.w}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="sm:rtl:ml-5 sm:w-1/3">
        {address && (
          <div className="bg-white border-1 flex items-center last-child-mb-0 mb-5 py-3 px-5 rounded-tiny">
            <Address {...address} className="flex-1" />
            {mapUrl && (
              <TrackClick
                as={DynamicLink}
                className="leading-none p-4 rtl:-mr-4 text-gray-darker"
                data-testid="address-link"
                url={mapUrl}
                target="_blank"
                trackingData={{
                  categoryGA: 'resort',
                  actionGA: 'mapClick',
                  labelGA: 'GoogleMapPage',
                }}
              >
                <Icon name={ICONS.ARROW_FORTH} hasRtl width="0.625rem" />
              </TrackClick>
            )}
          </div>
        )}
        {!isEmpty(access) &&
          accessTitle && <h3 className="reset-heading text-base mb-4">{accessTitle}</h3>}
        <SeeMore
          seeMoreLabel={t('common.viewMore')}
          seeLessLabel={t('common.loadLess')}
          seeMoreSize={250}
          isCollapseEnabled={access.length > 3}
          classNameButton="rtl:text-right"
        >
          {access.map(({ iconName, texts = [] }, i) => (
            <div
              className="bg-white border-1 flex items-center justify-between last-child-mb-0 mb-4 py-3 px-5 rounded-tiny"
              data-testid="AccessAndMap-access"
              key={i}
            >
              <div>
                {texts.map((accessTextLine, j) => (
                  <p
                    className={classnames('reset-paragraph leading-normal text-gray-darker', {
                      'text-sm': j,
                      'text-base': !j,
                    })}
                    key={j}
                  >
                    {j ? accessTextLine : <strong className="font-bold">{accessTextLine}</strong>}
                  </p>
                ))}
              </div>
              <Icon
                className="rtl:ml-5 flex-shrink-0"
                color={COLORS.GRAY_DARKER}
                name={iconName}
                width="1rem"
              />
            </div>
          ))}
        </SeeMore>
      </div>
    </div>
  );
}
